import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [doneOk, setDoneOk] = useState(false);

  const valid = token.length > 0 && password.length >= 8 && password === confirm;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      await apiClient.resetPassword(token, password);
      setDoneOk(true);
      toast.success('Senha redefinida com sucesso!');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err: any) {
      const code = err?.response?.data?.error;
      let msg = 'Falha ao redefinir senha. Tente solicitar um novo link.';
      if (code === 'invalid_or_expired_token') {
        msg = 'Este link expirou ou já foi usado. Volte em "Esqueci minha senha" e solicite um novo.';
      } else if (code === 'weak_password') {
        msg = 'A senha precisa ter pelo menos 8 caracteres.';
      } else if (code === 'missing_fields') {
        msg = 'Preencha a senha.';
      }
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Definir nova senha</h1>
          <p className="mt-2 text-muted-foreground">Escolha uma senha com pelo menos 8 caracteres.</p>
        </div>

        {!token ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900 flex gap-2">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
            <div>
              <strong className="block mb-1">Link inválido</strong>
              O token de redefinição não foi encontrado na URL. Volte em &quot;Esqueci minha senha&quot; e solicite um novo link.
            </div>
          </div>
        ) : doneOk ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 flex gap-2">
            <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
            <div>
              <strong className="block mb-1">Senha alterada</strong>
              Redirecionando para o login…
            </div>
          </div>
        ) : (
          <>
            {errorMsg && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900 flex gap-2">
                <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                <div>{errorMsg}</div>
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="Nova senha"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                autoFocus
              />
              <Input
                type="password"
                placeholder="Confirme a nova senha"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
              />
              {password.length > 0 && password.length < 8 && (
                <p className="text-xs text-red-600">Mínimo 8 caracteres.</p>
              )}
              {confirm.length > 0 && password !== confirm && (
                <p className="text-xs text-red-600">As senhas não conferem.</p>
              )}
              <Button type="submit" className="w-full" loading={loading} disabled={!valid}>
                Redefinir senha
              </Button>
            </form>
          </>
        )}

        <div className="text-center space-y-1">
          <Link to="/esqueci-senha" className="text-sm text-brand-600 hover:underline block">
            Solicitar novo link
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:underline block">
            ← Voltar pro login
          </Link>
        </div>
      </div>
    </div>
  );
}
